import React from "react";
import { useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Link, useNavigate } from "react-router-dom";
import { Web3ContextType } from "../../providers/context.types";
import { Web3Context } from "../../providers/web3.context";
import { AppState } from "../../store/root.reducer";
import * as AuthActions from "./store/auth.actions";

import { Input, Form } from "../../components/molecules/Form";
import InfoBox from "../../components/molecules/InfoBox";
import Heading from "../../components/atoms/Heading";
import { validateEmail } from "../../utils/textUtils";
import styles from '../../components/modals/modal.module.css';

export const RegisterPage = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // state
  const [email, setEmail] = useState<string>();
  const [password, setPassword] = useState<string>();
  const [name, setName] = useState<string>();

  // Track if fields have been modified to hide errors on typing
  const [nameTouched, setNameTouched] = useState<boolean>(false);
  const [emailTouched, setEmailTouched] = useState<boolean>(false);
  const [passwordTouched, setPasswordTouched] = useState<boolean>(false);

  const auth = useSelector((state: AppState) => state.auth);

  // Parse auth errors for field-specific display
  const getFieldError = (fieldName: string) => {
    if (!auth.authError) return null;
    
    // Hide errors if user has started typing in the field
    if (fieldName === 'name' && nameTouched) return null;
    if (fieldName === 'email' && emailTouched) return null;
    if (fieldName === 'password' && passwordTouched) return null;
    
    // Check if auth.authError is already an object
    if (typeof auth.authError === 'object') {
      const fieldError = auth.authError[fieldName];
      
      // Ensure we return a string, not an object
      if (fieldError) {
        if (typeof fieldError === 'string') {
          return fieldError;
        } else if (typeof fieldError === 'object') {
          // If it's an object, try to extract message or convert to string
          return fieldError.message || fieldError.error || JSON.stringify(fieldError);
        }
        return String(fieldError);
      } else if (auth.authError.msg && fieldName === 'email') {
        return auth.authError.msg;
      }
      return null;
    }
    
    // If it's a string, try to parse as JSON
    if (typeof auth.authError === 'string') {
      try {
        // Try to parse as JSON object for field-specific errors
        const errorObj = JSON.parse(auth.authError);
        const fieldError = errorObj[fieldName];
        
        // Ensure we return a string, not an object
        if (fieldError) {
          if (typeof fieldError === 'string') {
            return fieldError;
          } else if (typeof fieldError === 'object') {
            // If it's an object, try to extract message or convert to string
            return fieldError.message || fieldError.error || JSON.stringify(fieldError);
          }
          return String(fieldError);
        }
        return null;
      } catch {
        // If not JSON, treat as general error for email field (most common case)
        if (fieldName === 'email') {
          return auth.authError;
        }
        return null;
      }
    }
    
    return null;
  };

  const onSubmit = (e) => {
    e.preventDefault();
    if (!auth.loading) {
      dispatch(
        AuthActions.StartSignup({
          email,
          name,
          password,
          navigate,
        })
      );
    }
  };

  React.useEffect(() => {
    dispatch(AuthActions.ResetErrorMsg());
  }, []);

  // Reset touched states when a new error comes in
  React.useEffect(() => {
    if (auth.authError) {
      setNameTouched(false);
      setEmailTouched(false);
      setPasswordTouched(false);
    }
  }, [auth.authError]);

  // Custom onChange handlers that track touched state
  const handleNameChange = (value: string) => {
    setName(value);
    setNameTouched(true);
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    setEmailTouched(true);
  };

  const handlePasswordChange = (value: string) => {
    setPassword(value);
    setPasswordTouched(true);
  };

  return (
    <>
      <div className="container">
        <Heading h2 className="mb-1">
          Sign up
        </Heading>

        <Form onSubmit={onSubmit}>
          <Input
            type="text"
            title="Name/Company name"
            autocomplete="name"
            required
            placeholder="Enter name"
            state={{
              error: getFieldError('name') ? {
                condition: true,
                text: getFieldError('name'),
              } : null,
              details: !getFieldError('name') ? {
                condition: true,
                text: "User must be unique",
              } : null,
            }}
            value={name || ""}
            onChange={handleNameChange}
          />
          <Input
            type="text"
            title="Email"
            required
            placeholder="Your email"
            state={{
              error: getFieldError('email') ? {
                condition: true,
                text: getFieldError('email'),
              } : {
                condition: email !== undefined && !validateEmail(email),
                text: "Invalid email format",
              },
              details: !getFieldError('email') && (email === undefined || validateEmail(email)) ? {
                condition: true,
                text: "Email will require verification",
              } : null,
            }}
            value={email || ""}
            onChange={handleEmailChange}
          />

          <Input
            type="password"
            autocomplete="new-password"
            title="Password"
            required
            placeholder="Account password"
            state={{
              error: getFieldError('password') ? {
                condition: true,
                text: getFieldError('password'),
              } : {
                condition: password === "" || password?.length < 6,
                text: "Too short",
              },
              warning: !getFieldError('password') ? {
                condition:
                  password !== undefined &&
                  password.length <= 8 &&
                  password.length > 5,
                text: "Weak",
              } : null,
              success: !getFieldError('password') ? {
                condition: password !== undefined && password.length > 8,
                text: `Strong`,
              } : null,
              details: !getFieldError('password') ? {
                condition: true,
                text: "Make it a tough one",
              } : null,
            }}
            value={password || ""}
            onChange={handlePasswordChange}
          />

          <button
            type="submit"
            className={`${styles['btn']} ${styles['btn-primary']} mt-1`}
            disabled={auth.loading}
          >
            {auth.loading ? "REGISTERING..." : "REGISTER"}
          </button>

          <div className="mt-4">
            <Link to={"/auth/login"} style={{ textDecoration: 'none' }}>Already have an account? Log in</Link>
          </div>
        </Form>
      </div>
    </>
  );
};
